import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'

import useHistoryBreadcrumb from 'v2/views/utils/useHistoryBreadcrumb'

import { getUrl } from 'app/UrlService'
import { useAppUserContext } from 'app/useAppUserContext'
import { createRecord, invalidateRecord } from 'data/hooks/records/recordOperations'
import { useRealtimeObjectUpdates, useRealtimeRecordUpdates } from 'data/realtime/realtimeUpdates'
import { useBreadcrumbsContext } from 'features/breadcrumbs/hooks/useBreadcrumbsContext'
import { BANNER_HEIGHT } from 'features/core/appBannerConstants'
import { useIsBannerShowing } from 'features/core/useIsBannerShowing'
import { PathContext } from 'features/utils/PathContext'
import { useLayoutEditorContext } from 'features/views/LayoutEditor/useLayoutEditorContext'
import { RecordManagerContextProvider } from 'features/views/RecordManager/RecordManagerContext'
import { useRecordManagerContext } from 'features/views/RecordManager/useRecordManagerContext'
import useWindowTitle from 'utils/useWindowTitle'

import { useToast } from 'ui/components/Toast'
import { useResponsiveValue } from 'ui/styling/helpers/useResponsiveValue'

import { DetailViewContext } from './hooks/useDetailViewContext'
import { DeleteRecordConfirmModal } from './DeleteRecordConfirmModal'
import { getDetailViewHeader, getDetailViewRequiredFields } from './utils'

export type DetailViewContextProviderProps = {
    recordSid?: string
    isEmbedded?: boolean
    isSingleRecordListView?: boolean
}

export const DetailViewContextProvider: React.FC<DetailViewContextProviderProps> = ({
    children,
    recordSid: providedRecordSid,
    isEmbedded = false,
    isSingleRecordListView = true,
}) => {
    const {
        requiredFields = [],
        view,
        fields,
        isEditing,
        isInitialized,
        object,
    } = useLayoutEditorContext()
    const { pageUrl, recordId: pathRecordId } = useContext(PathContext)

    const [isEnabled, setIsEnabled] = useState(false)
    useEffect(() => {
        // Prevent loading record before the editor is initialized and the fields are loaded.
        if (isInitialized && !!fields) {
            requestAnimationFrame(() => {
                setIsEnabled(true)
            })
        }
    }, [isInitialized, fields])

    const effectiveRecordSid = providedRecordSid || pathRecordId
    // Disable record loading until the editor is initialized and the fields are loaded.
    const recordSid = isEnabled ? effectiveRecordSid : undefined

    const detailViewRequiredFields = getDetailViewRequiredFields(view, fields)
    const includeFields = isEditing
        ? undefined
        : Array.from(new Set([...detailViewRequiredFields, ...requiredFields]))

    const showSuccessToast = view?.layout?.showToastOnEdit ?? false

    return (
        <RecordManagerContextProvider
            recordSid={recordSid}
            includeFields={includeFields}
            showSuccessToast={showSuccessToast}
        >
            <DetailViewContextProviderInner
                object={object as ObjectDto}
                pageUrl={pageUrl}
                includedFields={requiredFields}
                isEmbedded={isEmbedded}
                isSingleRecordListView={isSingleRecordListView}
            >
                {children}
            </DetailViewContextProviderInner>
        </RecordManagerContextProvider>
    )
}

type DetailViewContextProviderInnerProps = {
    object?: ObjectDto
    pageUrl?: string
    includedFields?: string[]
    isEmbedded?: boolean
    isSingleRecordListView?: boolean
}

const DetailViewContextProviderInner: React.FC<DetailViewContextProviderInnerProps> = React.memo(
    function DetailViewContextProviderInner({
        children,
        object,
        pageUrl,
        includedFields = [],
        isEmbedded = false,
        isSingleRecordListView = false,
    }) {
        const includedFieldsRef = useRef(includedFields)
        includedFieldsRef.current = includedFields

        const { view, stack, fields, isEditing } = useLayoutEditorContext()

        const recordManager = useRecordManagerContext()
        const recordRef = useRef(recordManager.record)
        recordRef.current = recordManager.record

        const header = getDetailViewHeader(view?.layout, fields)

        const viewPath = pageUrl

        const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false)
        const deleteRecord = useCallback(() => {
            setIsDeleteModalOpen(true)
        }, [])

        const history = useHistory()
        const toast = useToast()
        const duplicateRecord = useCallback(async () => {
            const record = recordRef.current
            if (!record || !object?._sid) return

            try {
                const recordClone = { ...record, _sid: null, object_id: object?._sid }

                const newRecord = await createRecord(recordClone, includedFieldsRef.current, false)

                toast({
                    title: 'Record duplicated successfully',
                    startIcon: { name: 'CheckCircle2' },
                    type: 'success',
                })

                const newViewPath = getUrl(`${object?.url}/view/${newRecord?._sid}`)
                history.push(newViewPath)
            } catch {
                toast({
                    title: 'Failed to duplicate record',
                    startIcon: { name: 'AlertCircle' },
                    type: 'error',
                })
            }
        }, [history, object?._sid, object?.url, toast])

        const [isPreviewing] = useIsBannerShowing()

        useHistoryBreadcrumb(
            {
                title: recordManager.record?._primary,
                type: 'detail',
                objectId: view?.object_id,
            },
            !!isEmbedded || isSingleRecordListView
        )

        useWindowTitle(recordManager.record?._primary, !!isEmbedded || isSingleRecordListView)

        const { breadcrumbs, prevItem } = useBreadcrumbsContext()

        let contentWidth = view?.layout?.contentWidth ?? 'full'
        if (isEmbedded) {
            contentWidth = 'full'
        }
        const widgetStyle = view?.layout?.type ?? 'compact'

        const recordSid = recordManager.record?._sid
        const objectSid = object?._sid

        useRealtimeRecordUpdates({
            stack: stack!,
            recordSid: recordSid!,
            handler: () => {
                invalidateRecord(recordSid!)
            },
            disabled: !recordSid || !stack,
        })

        useRealtimeObjectUpdates({
            stack: stack!,
            objectIds: [objectSid!],
            handler: () => {
                invalidateRecord(recordSid!)
            },
            disabled: !objectSid || !stack,
        })

        let topOffset = isPreviewing ? `${BANNER_HEIGHT}px` : undefined
        if (isEmbedded) {
            topOffset = undefined
        }

        const { isAdmin } = useAppUserContext()

        const rightOffset = useResponsiveValue<string | undefined>({
            mobile: undefined,
            tablet: isAdmin ? '40px' : undefined,
        })

        const sidebarStyle = view?.layout?.sidebarStyle ?? 'none'
        const isSidebarSticky = view?.layout?.isSidebarSticky ?? false

        const value = useMemo(
            () => ({
                view,
                viewPath,
                recordManager,
                header,
                object,
                stack,
                deleteRecord,
                duplicateRecord,
                isPreviewing,
                breadcrumbs,
                prevBreadcrumb: prevItem,
                requestIncludedFields: includedFields,
                contentWidth,
                widgetStyle,
                isEmbedded,
                isEditing,
                fields,
                topOffset,
                rightOffset,
                sidebarStyle,
                isSidebarSticky,
            }),
            [
                view,
                viewPath,
                recordManager,
                header,
                object,
                stack,
                deleteRecord,
                duplicateRecord,
                isPreviewing,
                breadcrumbs,
                prevItem,
                includedFields,
                contentWidth,
                widgetStyle,
                isEmbedded,
                isEditing,
                fields,
                topOffset,
                rightOffset,
                sidebarStyle,
                isSidebarSticky,
            ]
        )

        return (
            <DetailViewContext.Provider value={value}>
                {children}
                <DeleteRecordConfirmModal
                    isOpen={isDeleteModalOpen}
                    onOpenChange={setIsDeleteModalOpen}
                />
            </DetailViewContext.Provider>
        )
    }
)
